<template>
    <div
        class="modal fade"
        id="itemCompendiumModal"
        tabindex="-1"
        aria-hidden="true"
        ref="modal"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title">
                        {{ mode === "add" ? "Add Item" : "Edit Item" }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>

                <div class="modal-body">
                    <!-- Edit: pick existing -->
                    <div v-if="mode === 'edit'" class="input-group mb-3">
                        <span class="input-group-text">Select Item</span>
                        <select
                            v-model="selectedName"
                            @change="loadItem"
                            class="form-select"
                        >
                            <option disabled value="">Select...</option>
                            <option
                                v-for="i in store.items.sort((a, b) =>
                                    a.name.localeCompare(b.name)
                                )"
                                :key="i.name"
                                :value="i.name"
                            >
                                {{ i.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Item form -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Name</span>
                        <input
                            v-model="item.name"
                            type="text"
                            class="form-control"
                        />
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Type</span>
                        <input
                            v-model="item.type"
                            type="text"
                            class="form-control"
                        />
                    </div>

                    <!-- Abilities -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Ability</span>
                        <select v-model="selectedAbility" class="form-select">
                            <option disabled value="">Select...</option>
                            <option
                                v-for="a in store.itemAbilities.sort((a, b) =>
                                    a.name.localeCompare(b.name)
                                )"
                                :key="a.name"
                                :value="a.name"
                            >
                                {{ a.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addAbility"
                        >
                            Add
                        </button>
                    </div>

                    <div class="input-group mb-3">
                        <span class="input-group-text">Custom Ability</span>
                        <input
                            v-model="customAbilityName"
                            type="text"
                            class="form-control"
                            placeholder="Name"
                        />
                        <input
                            v-model="customAbilityDesc"
                            type="text"
                            class="form-control"
                            placeholder="Description"
                        />
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addCustomAbility"
                        >
                            Add
                        </button>
                    </div>

                    <!-- Selected Abilities -->
                    <div v-if="item.ability.length" class="d-flex flex-wrap gap-2">
                        <span
                            v-for="(ab, i) in item.ability"
                            :key="i"
                            class="badge bg-success d-flex align-items-center"
                        >
                            {{ ab }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeAbility(i)"
                            ></button>
                        </span>
                    </div>
                </div>

                <p class="px-3">
                    <strong>
                        Note: These changes won't affect any items currently added to your character. 
                        You will need to remove and re-add the item for the changes to take effect.
                    </strong>
                </p>

                <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal">
                        <font-awesome-icon icon="multiply" class="me-1" /> Close
                    </button>
                    <button
                        v-if="mode === 'edit'"
                        class="btn btn-danger me-1"
                        @click="deleteItem"
                    >
                        <font-awesome-icon icon="trash-alt" class="me-1" /> Delete
                    </button>
                    <button class="btn btn-primary" @click="saveItem">
                        <font-awesome-icon icon="save" class="me-1" /> Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRosterStore } from "@/stores/index";

export default {
    name: "ItemCompendiumModal",
    props: { mode: { type: String, default: "add" } },
    data() {
        return {
            store: useRosterStore(),
            selectedName: "",
            item: { name: "", type: "", ability: [] },
            selectedAbility: "",
            customAbilityName: "",
            customAbilityDesc: "",
        };
    },
    mounted() {
        this.$refs.modal.addEventListener("show.bs.modal", () => {
            this.resetForm();
        });
    },
    methods: {
        resetForm() {
            this.selectedName = "";
            this.item = { name: "", type: "", ability: [] };
            this.selectedAbility = "";
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        loadItem() {
            const found = this.store.items.find(
                (i) => i.name === this.selectedName
            );
            if (found) this.item = JSON.parse(JSON.stringify(found));
        },
        saveItem() {
            if (!this.item.name.trim()) {
                alert("Item must have a name!");
                return;
            }
            if (!this.item.type.trim()) {
                alert("Item must have a type!");
                return;
            }
            if (this.customAbilityName.trim() || this.customAbilityDesc.trim()) {
                alert(
                    "You typed a custom ability but didn’t add it. Please press Add first."
                );
                return;
            }
            if (this.selectedAbility !== "") {
                alert(
                    "You selected an ability but didn’t add it. Please press Add first."
                );
                return;
            }

            if (this.mode === "add") {
                this.store.items.push(this.item);
                alert("Item added!");
            } else {
                const idx = this.store.items.findIndex(
                    (i) => i.name === this.selectedName
                );
                if (idx !== -1) this.store.items.splice(idx, 1, this.item);
                alert("Item updated!");
            }
            this.resetForm();
        },
        deleteItem() {
            if (!this.selectedName) return;
            if (confirm(`Delete item "${this.selectedName}"?`)) {
                const idx = this.store.items.findIndex(
                    (i) => i.name === this.selectedName
                );
                if (idx !== -1) this.store.items.splice(idx, 1);
            }
            this.resetForm();
            alert("Item deleted!");
        },
        addAbility() {
            if (!this.selectedAbility) return;
            if (!this.item.ability.includes(this.selectedAbility)) {
                this.item.ability.push(this.selectedAbility);
            }
            this.selectedAbility = "";
        },
        addCustomAbility() {
            const name = this.customAbilityName.trim();
            const desc = this.customAbilityDesc.trim();
            if (!name || !desc) {
                alert("Custom ability must have both Name and Description!");
                return;
            }
            // Add to item
            this.item.ability.push(name);
            // Add to store so it’s available in the list
            this.store.itemAbilities.push({ name, description: desc });
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        removeAbility(i) {
            this.item.ability.splice(i, 1);
        },
    },
};
</script>

